import React from "react";

import { css, cx } from "@emotion/css";
import Divider from "@mui/material/Divider";
import { Editor } from "@tiptap/core";
import { Level } from "@tiptap/extension-heading";

import { FontSizes } from "@/assets/fonts.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Theme, useTheme } from "@/theme/CreateTheme.ts";
import { HeadingSelectButton, HeadingType } from "@/uiKit/molecules/RichText/inputs/HeadingSelectButton.tsx";
import { RichTextFormattingButtons } from "@/uiKit/molecules/RichText/RichTextFormattingButtons.tsx";

export type RichTextEditionHeaderProps = {
  id: string;
  editor: Editor;
  className?: string;
};

export const RichTextEditionHeader: React.FC<RichTextEditionHeaderProps> = ({ id, editor, className }) => {
  const styles = useStyles(makeStyles);
  const theme = useTheme();
  return (
    <div className={cx(styles.container, className)} id={id}>
      <HeadingSelectButton
        popupId={`${id}-heading-menu`}
        heading={
          editor.getAttributes("heading")["level"]
            ? (`h${editor.getAttributes("heading")["level"]}` as HeadingType)
            : "normal"
        }
        onSelectHeading={heading => {
          switch (heading) {
            case "normal":
              editor
                .chain()
                .focus()
                .toggleHeading({
                  level: editor.getAttributes("heading")["level"],
                })
                .run();
              break;
            default: {
              const value = Number.parseInt(heading.replace("h", ""));
              if (!isNaN(value)) {
                editor
                  .chain()
                  .focus()
                  .setHeading({ level: value as Level })
                  .run();
              }
              break;
            }
          }
        }}
      />
      <Divider orientation="vertical" flexItem />
      <RichTextFormattingButtons
        menuIdPrefix={id}
        toggled={{
          bold: editor.isActive("bold"),
          italic: editor.isActive("italic"),
          underlined: editor.isActive("underline"),
        }}
        onToggle={value => {
          switch (value) {
            case "bold":
              editor.chain().focus().toggleBold().run();
              break;
            case "italic":
              editor.chain().focus().toggleItalic().run();
              break;
            case "underlined":
              editor.chain().focus().toggleUnderline().run();
              break;
          }
        }}
        color={editor.getAttributes("textStyle")["color"] ?? theme.palette.common.black}
        onSelectColor={color => editor.chain().focus().setColor(color).run()}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "row",
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(4),
  }),
  icon: css({
    width: FontSizes.bodyLarge,
    height: FontSizes.bodyLarge,
  }),
});
